import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, RefreshCw, RotateCcw, Send, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import {
  fetchNotificationMeta,
  fetchNotificationTemplate,
  fetchNotificationTemplates,
  improveNotificationCopy,
  previewNotificationTemplate,
  resetNotificationTemplateOverride,
  testNotificationTemplate,
  updateNotificationTemplate,
} from "@/lib/notification-templates-api";

const CATEGORY_COLORS = {
  information: "#3B82F6",
  success: "#22C55E",
  warning: "#F59E0B",
  error: "#EF4444",
  promotion: "#A855F7",
  billing: "#1E3A8A",
  ai: "#8B5CF6",
  security: "#52525B",
};

export default function SystemNotificationTemplates() {
  const [meta, setMeta] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [group, setGroup] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [metaRes, listRes] = await Promise.all([
        meta ? Promise.resolve(meta) : fetchNotificationMeta(),
        fetchNotificationTemplates(group || undefined),
      ]);
      if (!meta) setMeta(metaRes);
      setTemplates(listRes.templates || []);
      setStats(listRes.stats || null);
      if (!selectedId && listRes.templates?.length) {
        setSelectedId(listRes.templates[0].triggerId);
      }
    } catch (err) {
      toast.error("Could not load templates", { description: err.message });
    } finally {
      setLoading(false);
    }
  }, [group, meta]);

  const loadDetail = useCallback(async (triggerId) => {
    if (!triggerId) return;
    try {
      const res = await fetchNotificationTemplate(triggerId);
      setDetail(res);
      const tpl = res.template || {};
      setDraft({
        subject: tpl.subject || "",
        previewText: tpl.previewText || "",
        bodyHtml: tpl.bodyHtml || tpl.bodyText || "",
        bodyText: tpl.bodyText || "",
        category: tpl.category || "information",
        enabled: tpl.enabled !== false,
        subjectB: tpl.subjectB || "",
        abEnabled: !!tpl.abEnabled,
      });
      const prev = await previewNotificationTemplate(triggerId, { toEmail: "", context: {}, locale: "en" });
      setPreviewHtml(prev.htmlContent || "");
    } catch (err) {
      toast.error("Could not load template", { description: err.message });
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const groups = meta?.groups || [];
  const categories = meta?.categories || {};
  const variables = meta?.variables || [];

  const filtered = useMemo(() => templates, [templates]);

  const save = async () => {
    if (!selectedId || !draft) return;
    setSaving(true);
    try {
      const res = await updateNotificationTemplate(selectedId, draft);
      toast.success("Template saved");
      setDetail((prev) => ({ ...prev, template: res.template }));
      loadList();
    } catch (err) {
      toast.error("Save failed", { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const resetOverride = async () => {
    if (!selectedId) return;
    try {
      await resetNotificationTemplateOverride(selectedId);
      toast.success("Reset to platform default");
      loadDetail(selectedId);
      loadList();
    } catch (err) {
      toast.error("Reset failed", { description: err.message });
    }
  };

  const runImprove = async () => {
    if (!draft?.bodyHtml) return;
    try {
      const res = await improveNotificationCopy(draft.bodyHtml);
      setDraft((d) => ({ ...d, bodyHtml: res.body, bodyText: res.body }));
      toast.success("Copy improved");
    } catch (err) {
      toast.error("Improve failed", { description: err.message });
    }
  };

  const runTest = async () => {
    if (!selectedId || !testEmail) {
      toast.error("Enter a test email address");
      return;
    }
    try {
      await testNotificationTemplate(selectedId, { toEmail: testEmail, context: {}, locale: "en" });
      toast.success("Test email sent", { description: testEmail });
    } catch (err) {
      toast.error("Test send failed", { description: err.message });
    }
  };

  const refreshPreview = async () => {
    if (!selectedId || !draft) return;
    try {
      await updateNotificationTemplate(selectedId, draft);
      const prev = await previewNotificationTemplate(selectedId, { toEmail: "", context: {}, locale: "en" });
      setPreviewHtml(prev.htmlContent || "");
    } catch (err) {
      toast.error("Preview failed", { description: err.message });
    }
  };

  const tpl = detail?.template;
  const tplStats = detail?.stats;

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1600px] mx-auto" data-testid="system-notification-templates">
      <Link to="/admin/settings" className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to settings
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-overline">Workspace · notifications</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium flex items-center gap-3">
            <Mail className="w-8 h-8 text-[var(--primary)]" />
            System email templates
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
            Edit transactional emails sent by AivoryEMS. Defaults apply to all workspaces; your changes override them for this tenant.
          </p>
        </div>
        {stats ? (
          <div className="surface px-4 py-3 text-xs font-mono text-[var(--text-secondary)]">
            Sent {stats.sent || 0} · Opened {stats.opened || 0} · Clicked {stats.clicked || 0}
          </div>
        ) : null}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        <aside className="surface p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <label className="block">
            <span className="text-overline">Category group</span>
            <select className="input-dark mt-1 w-full text-sm" value={group} onChange={(e) => setGroup(e.target.value)}>
              <option value="">All groups</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.label}</option>
              ))}
            </select>
          </label>
          {loading ? (
            <div className="text-sm text-[var(--text-tertiary)]">Loading…</div>
          ) : (
            <ul className="divide-y divide-[var(--border-default)]">
              {filtered.map((row) => (
                <li key={row.triggerId}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(row.triggerId)}
                    className={`w-full text-left px-2 py-2 rounded text-sm ${selectedId === row.triggerId ? "bg-[var(--surface-elevated)]" : "hover:bg-[var(--surface-elevated)]/60"}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: CATEGORY_COLORS[row.category] || "#94a3b8" }} />
                      <span className="font-medium truncate">{row.name}</span>
                      {row.hasOverride ? <span className="text-[10px] uppercase text-[var(--primary)]">Custom</span> : null}
                    </div>
                    <div className="text-[11px] text-[var(--text-tertiary)] truncate mt-0.5">{row.subject}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <main className="space-y-6">
          {!draft || !tpl ? (
            <div className="surface p-8 text-[var(--text-secondary)]">Select a template to edit.</div>
          ) : (
            <>
              <div className="surface p-5 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="font-heading text-2xl">{tpl.name}</h2>
                    <p className="text-xs font-mono text-[var(--text-tertiary)] mt-1">{tpl.triggerId}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="btn-secondary text-sm" onClick={resetOverride} disabled={!tpl.hasOverride}>
                      <RotateCcw className="w-4 h-4" /> Reset default
                    </button>
                    <button type="button" className="btn-secondary text-sm" onClick={refreshPreview}>
                      <RefreshCw className="w-4 h-4" /> Preview
                    </button>
                    <button type="button" className="btn-primary text-sm" onClick={save} disabled={saving}>
                      {saving ? "Saving…" : "Save override"}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label className="block">
                    <span className="text-overline">Category</span>
                    <select
                      className="input-dark mt-1 w-full"
                      value={draft.category}
                      onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    >
                      {Object.keys(categories).map((key) => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block md:col-span-2 flex items-center gap-2 mt-6">
                    <input
                      type="checkbox"
                      checked={draft.enabled}
                      onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                    />
                    <span className="text-sm">Enabled</span>
                  </label>
                </div>

                <label className="block">
                  <span className="text-overline">Subject line</span>
                  <input className="input-dark mt-1 w-full" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
                </label>
                <label className="block">
                  <span className="text-overline">Preview text</span>
                  <input className="input-dark mt-1 w-full" value={draft.previewText} onChange={(e) => setDraft({ ...draft, previewText: e.target.value })} />
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-overline">A/B subject B</span>
                    <input className="input-dark mt-1 w-full" value={draft.subjectB} onChange={(e) => setDraft({ ...draft, subjectB: e.target.value })} />
                  </label>
                  <label className="block flex items-center gap-2 mt-6">
                    <input type="checkbox" checked={draft.abEnabled} onChange={(e) => setDraft({ ...draft, abEnabled: e.target.checked })} />
                    <span className="text-sm">A/B test subjects</span>
                  </label>
                </div>

                <label className="block">
                  <div className="flex items-center justify-between">
                    <span className="text-overline">Body (HTML or plain text)</span>
                    <button type="button" className="text-xs text-[var(--primary)] inline-flex items-center gap-1" onClick={runImprove}>
                      <Wand2 className="w-3 h-3" /> Improve copy
                    </button>
                  </div>
                  <textarea
                    className="input-dark mt-1 w-full min-h-[160px] font-mono text-sm"
                    value={draft.bodyHtml}
                    onChange={(e) => setDraft({ ...draft, bodyHtml: e.target.value, bodyText: e.target.value })}
                  />
                </label>

                <div>
                  <span className="text-overline">Variables</span>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {variables.map((v) => (
                      <code key={v} className="text-[10px] px-1.5 py-0.5 bg-[var(--surface-elevated)] rounded">{`{{${v}}}`}</code>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-[var(--border-default)]">
                  <label className="block flex-1 min-w-[200px]">
                    <span className="text-overline">Send test to</span>
                    <input className="input-dark mt-1 w-full font-mono text-sm" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@company.com" />
                  </label>
                  <button type="button" className="btn-secondary" onClick={runTest}>
                    <Send className="w-4 h-4" /> Send test
                  </button>
                </div>

                {tplStats ? (
                  <div className="text-xs font-mono text-[var(--text-tertiary)]">
                    Delivery: sent {tplStats.sent || 0} · opened {tplStats.opened || 0} · bounced {tplStats.bounced || 0}
                  </div>
                ) : null}

                {(detail?.versions || []).length ? (
                  <div>
                    <div className="text-overline">Version history</div>
                    <ul className="mt-2 text-xs font-mono space-y-1 max-h-24 overflow-y-auto">
                      {detail.versions.map((v) => (
                        <li key={v.id} className="text-[var(--text-secondary)]">
                          v{v.version} · {v.actorEmail || "system"} · {v.createdAt}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>

              <div className="surface p-5">
                <div className="text-overline flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4" /> Brand preview
                </div>
                {previewHtml ? (
                  <iframe title="Email preview" className="w-full min-h-[420px] bg-white rounded border border-[var(--border-default)]" srcDoc={previewHtml} />
                ) : (
                  <p className="text-sm text-[var(--text-tertiary)]">Save or refresh preview to see rendered email.</p>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
